import React, { useState, useEffect, useContext } from 'react';
import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';
import { AuthContext } from '../../context/AuthContext';
import { useSidebarState } from '../../components/sidebar/SidebarStateContext';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab
} from '@mui/material';
import { 
  DataGridPro,
  GridToolbar
} from '@mui/x-data-grid-pro';
import { 
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  Computer as ComputerIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import './Logs.scss';

// Importar los datos del JSON
import prestacionesAuxiliaresData from '../../login_logs_prestaciones_auxiliares_valenciana_reduced.json';
import praconServiextData from '../../login_logs_pracon_serviext_reduced.json';
import telimanData from '../../login_logs_teliman_reduced.json';
import scuPracomAndaluciaData from '../../login_logs_scu_pracom_andalucia_reduced.json';

const Logs = () => {
  const navigate = useNavigate();
  // Removido AuthContext ya que ahora es una ruta pública
  const { collapsed } = useSidebarState();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDevice, setFilterDevice] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState('pracon-serviext');
  const [currentData, setCurrentData] = useState(null);

  // Agregar una clase basada en el estado de colapso
  const containerClass = collapsed ? "listContainer collapsed" : "listContainer";

  // Inicializar datos al cargar el componente
  useEffect(() => {
    setCurrentData(praconServiextData);
  }, []);

  useEffect(() => {
    // Cambiar datos cuando cambia la empresa seleccionada
    let newData;
    if (selectedCompany === 'pracon-serviext') {
      newData = praconServiextData;
    } else if (selectedCompany === 'prestaciones-auxiliares') {
      newData = prestacionesAuxiliaresData;
    } else if (selectedCompany === 'teliman') {
      newData = telimanData;
    } else if (selectedCompany === 'scu-pracom-andalucia') {
      newData = scuPracomAndaluciaData;
    }
    
    if (newData) {
      setCurrentData(newData);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (currentData) {
      loadLoginLogs();
    }
  }, [currentData]);

  useEffect(() => {
    filterLogs();
  }, [logs, searchTerm, filterDevice]);

  const loadLoginLogs = () => {
    setLoading(true);
    try {
      console.log('Loading logs for company:', selectedCompany);
      console.log('Current data:', currentData?.empresa_nombre, 'Total users:', currentData?.total_usuarios_unicos);
      
      // Procesar los datos del JSON actual
      const processedLogs = currentData.login_logs.map((log, index) => ({
        id: log._id || index,
        email: log.email,
        timestamp: log.timestamp,
        ipAddress: log.ip_address,
        userAgent: log.user_agent,
        sessionId: log.session_id,
        userId: log.user_id,
        source: log.source,
        level: log.level,
        content: log.content,
        extractedAt: log._extracted_at,
        fullDocument: log.full_document,
        // Campos calculados para mejor visualización
        deviceType: getDeviceType(log.user_agent),
        browser: getBrowser(log.user_agent),
        os: getOperatingSystem(log.user_agent),
        isMobile: isMobileDevice(log.user_agent),
        date: new Date(log.timestamp).toLocaleDateString('es-ES'),
        time: new Date(log.timestamp).toLocaleTimeString('es-ES'),
        status: 'success', // Todos los logs son de login exitoso
        // URL para ver detalles individuales - ahora usa /logs en lugar de /login-logs
        detailUrl: `https://www.test-sentry.bepart-innova.com/logs/${selectedCompany}/${log._id || index}`
      }));

      setLogs(processedLogs);
      setFilteredLogs(processedLogs);
    } catch (error) {
      console.error('Error loading login logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Funciones auxiliares para procesar user agent
  const getDeviceType = (userAgent) => {
    if (userAgent.includes('Mobile') || userAgent.includes('iPhone') || userAgent.includes('Android')) {
      return 'Mobile';
    } else if (userAgent.includes('Tablet') || userAgent.includes('iPad')) {
      return 'Tablet';
    }
    return 'Desktop';
  };

  const getBrowser = (userAgent) => {
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  };

  const getOperatingSystem = (userAgent) => {
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac OS X')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('iPhone OS')) return 'iOS';
    if (userAgent.includes('Android')) return 'Android';
    return 'Unknown';
  };

  const isMobileDevice = (userAgent) => {
    return userAgent.includes('Mobile') || userAgent.includes('iPhone') || userAgent.includes('Android');
  };

  const filterLogs = () => {
    let filtered = logs;

    // Filtrar por término de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.ipAddress.includes(searchTerm) ||
        log.browser.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.os.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por dispositivo
    if (filterDevice !== 'all') {
      filtered = filtered.filter(log => log.deviceType === filterDevice);
    }

    setFilteredLogs(filtered);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getDeviceColor = (deviceType) => {
    switch (deviceType) {
      case 'Mobile':
        return 'primary';
      case 'Tablet':
        return 'secondary';
      case 'Desktop':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleViewDetails = (logId) => {
    navigate(`/logs/${selectedCompany}/${logId}`);
  };

  const handleCompanyChange = (event) => {
    console.log('Changing company to:', event.target.value);
    setSelectedCompany(event.target.value);
  };

  // Estadísticas
  const stats = {
    totalLogs: logs.length,
    uniqueUsers: new Set(logs.map(log => log.email)).size,
    mobileLogins: logs.filter(log => log.isMobile).length,
    desktopLogins: logs.filter(log => !log.isMobile).length,
    chromeUsers: logs.filter(log => log.browser === 'Chrome').length,
    firefoxUsers: logs.filter(log => log.browser === 'Firefox').length,
  };

  // Columnas para el DataGrid
  const columns = [
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            {params.row.email.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2" noWrap>
            {params.row.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'date',
      headerName: 'Fecha',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.date}
        </Typography>
      ),
    },
    {
      field: 'time',
      headerName: 'Hora',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.time}
        </Typography>
      ),
    },
    {
      field: 'deviceType',
      headerName: 'Dispositivo',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.deviceType}
          color={getDeviceColor(params.row.deviceType)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'browser',
      headerName: 'Navegador',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.browser}
        </Typography>
      ),
    },
    {
      field: 'os',
      headerName: 'Sistema Operativo',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.os}
        </Typography>
      ),
    },
    {
      field: 'ipAddress',
      headerName: 'IP',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.row.ipAddress}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          {getStatusIcon(params.row.status)}
          <Chip
            label="Login Exitoso"
            color={getStatusColor(params.row.status)}
            size="small"
            variant="filled"
          />
        </Box>
      ),
    },
    {
      field: 'detailUrl',
      headerName: 'URL de Acceso',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography 
            variant="body2" 
            fontFamily="monospace" 
            sx={{ 
              fontSize: '11px',
              color: 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': {
                color: 'primary.dark'
              }
            }}
            onClick={() => window.open(params.row.detailUrl, '_blank')}
          >
            {params.row.detailUrl}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => window.open(params.row.detailUrl, '_blank')}
            sx={{ color: 'primary.main' }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Tooltip title="Ver detalles">
            <IconButton 
              size="small" 
              onClick={() => handleViewDetails(params.row.id)}
              sx={{ color: 'primary.main' }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const TabPanel = ({ children, value, index, ...other }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`login-logs-tabpanel-${index}`}
        aria-labelledby={`login-logs-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            {children}
          </Box>
        )}
      </div>
    );
  };

  return (
    <div className="list">
      <Sidebar />
      <div className={containerClass}>
        <Navbar />
        
        <div className="logs-content">
          {/* Page Title */}
          <Typography variant="h4" component="h1" sx={{ color: '#1976d2', fontWeight: 600, marginBottom: 3 }}>
            Logs de acceso
          </Typography>

          {/* Company Selector */}
          <Card className="company-selector-card" sx={{ marginBottom: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
                    Seleccionar Empresa
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Empresa</InputLabel>
                    <Select
                      value={selectedCompany}
                      label="Empresa"
                      onChange={handleCompanyChange}
                    >
                      <MenuItem value="pracon-serviext">
                        PRACON SERVIEXT (68 usuarios)
                      </MenuItem>
                      <MenuItem value="prestaciones-auxiliares">
                        PRESTACIONES AUXILIARES VALENCIANA (116 usuarios)
                      </MenuItem>
                      <MenuItem value="teliman">
                        TELIMAN (116 usuarios)
                      </MenuItem>
                      <MenuItem value="scu-pracom-andalucia">
                        SCU PRACOM ANDALUCIA (68 usuarios)
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="login logs tabs">
              <Tab label="Registro de Accesos" />
              <Tab label="Estadísticas" />
              <Tab label="Análisis de Dispositivos" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <TabPanel value={tabValue} index={0}>
            {/* Actions Bar */}
            <Card className="actions-card">
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Buscar por email, IP, navegador..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        size="small"
                      >
                        Exportar
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        size="small"
                        onClick={loadLoginLogs}
                      >
                        Actualizar
                      </Button>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Dispositivo</InputLabel>
                        <Select
                          value={filterDevice}
                          label="Dispositivo"
                          onChange={(e) => setFilterDevice(e.target.value)}
                        >
                          <MenuItem value="all">Todos</MenuItem>
                          <MenuItem value="Desktop">Escritorio</MenuItem>
                          <MenuItem value="Mobile">Móvil</MenuItem>
                          <MenuItem value="Tablet">Tablet</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* DataGrid */}
            <Card className="logs-table-card">
              <CardContent sx={{ padding: 0 }}>
                <DataGridPro
                  rows={filteredLogs}
                  columns={columns}
                  pageSize={25}
                  rowsPerPageOptions={[25, 50, 100]}
                  checkboxSelection
                  disableSelectionOnClick
                  autoHeight
                  slots={{ toolbar: GridToolbar }}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                    },
                  }}
                />
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* Statistics Cards */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className="stat-card">
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="h4" color="primary">
                          {stats.totalLogs}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Total de Accesos
                        </Typography>
                      </Box>
                      <AccessTimeIcon color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className="stat-card">
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="h4" color="secondary">
                          {stats.uniqueUsers}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Usuarios Únicos
                        </Typography>
                      </Box>
                      <PeopleIcon color="secondary" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className="stat-card">
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="h4" color="success.main">
                          {stats.mobileLogins}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Accesos Móviles
                        </Typography>
                      </Box>
                      <ComputerIcon color="success" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className="stat-card">
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="h4" color="info.main">
                          {stats.desktopLogins}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Accesos Escritorio
                        </Typography>
                      </Box>
                      <ComputerIcon color="info" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Browser Statistics */}
            <Card className="stats-card">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Distribución por Navegador
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="body2">Chrome</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {stats.chromeUsers} ({((stats.chromeUsers / stats.totalLogs) * 100).toFixed(1)}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(stats.chromeUsers / stats.totalLogs) * 100}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="body2">Firefox</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {stats.firefoxUsers} ({((stats.firefoxUsers / stats.totalLogs) * 100).toFixed(1)}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(stats.firefoxUsers / stats.totalLogs) * 100}
                      color="secondary"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Análisis de Dispositivos
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Análisis detallado de los tipos de dispositivos utilizados para acceder al sistema.
                </Typography>
                {/* Aquí se pueden agregar más análisis específicos */}
              </CardContent>
            </Card>
          </TabPanel>
        </div>
      </div>
    </div>
  );
};

export default Logs;
